import { Fragment, memo } from 'react';

import { plural } from '../../../../../shared/helpers';
import Flex from '../../../generic/Flex';
import { Checkbox, LabeledCheckbox, TextInput } from '../../../generic/Forms';
import Loader from '../../../generic/Loader';
import type { RolePermissions } from '../../../../../shared/model-typings';
import type { PermissionGroup } from '../../../../../typings/api';
import type { RoleScope } from '../../../../../typings/enums';

import useAllPermissions, { permissionIsChecked } from './hooks/useAllPermissions';
import type { AllPermissionsItem } from './hooks/useAllPermissions';

type AllPermissionsTableHeaderProps = {
  isAllPermissionsChecked: boolean;
  searchQuery: string;
  onCheckAllPermissions: () => void;
  onSearchQueryChange: (value: string) => void;
};

/**
 * Header w/Search bar for searcing for permissions
 *
 * @param {AllPermissionsTableHeaderProps} params - The props for this component.
 * @param {boolean} params.isAllPermissionsChecked - If all available permissions are checked.
 * @param {string} params.searchQuery - The permission search query.
 * @param {() => void} params.onCheckAllPermissions - Checkbox change handler for checking/unchecking all.
 * @param {(value: string) => void} params.onSearchQueryChange - Change handler for the text input.
 */
const AllPermissionsTableHeader = ({
  isAllPermissionsChecked,
  searchQuery,
  onCheckAllPermissions,
  onSearchQueryChange,
}: AllPermissionsTableHeaderProps) => (
  <header className="roles__all-permissions-table__header">
    <Flex justify="flex-start" gap="8px">
      <Checkbox
        isChecked={isAllPermissionsChecked}
        onChange={onCheckAllPermissions}
        isRippleDisabled
      />
      <TextInput
        label="Find a permission"
        placeholder="Find a permission"
        value={searchQuery}
        onChange={onSearchQueryChange}
      />
    </Flex>
  </header>
);

type AllPermissionsTableFooterProps = {
  allCount: number;
  hiddenCount: number;
};

/**
 * Displays the number of currently selected permissions,
 * including the number which are hidden because of the search query.
 *
 * @param {AllPermissionsTableFooterProps} params - The props for this component.
 * @param {number} params.allCount - The number of permissions which are currently selected.
 * @param {number} params.hiddenCount - The number of permissions which are selected but not visible due to the search query.
 */
const AllPermissionsTableFooter = ({ allCount, hiddenCount }: AllPermissionsTableFooterProps) => (
  <footer className="roles__all-permissions-table__footer">
    <span>{`${allCount} ${plural(allCount, 'permission')} selected`}</span>
    {hiddenCount > 0 && (
      <span className="roles__all-permissions-table__footer__hidden-count">
        {`(${hiddenCount} ${plural(hiddenCount, 'permission')} hidden)`}
      </span>
    )}
  </footer>
);

type AllPermissionsTableProps = {
  isReadOnly: boolean;
  scope: RoleScope;
  rolePermissions: RolePermissions;
  onPermissionsChange: (permissions: AllPermissionsItem[]) => void;
};

const AllPermissionsTable = ({
  scope,
  isReadOnly,
  rolePermissions,
  onPermissionsChange,
}: AllPermissionsTableProps) => {
  const { state, dispatch, groups } = useAllPermissions({
    scope,
    isReadOnly,
    rolePermissions,
    onPermissionsChange,
  });

  return state.isLoading ? (
    <Loader />
  ) : (
    <div className="roles__all-permissions-table">
      <AllPermissionsTableHeader
        isAllPermissionsChecked={state.isAllPermissionsChecked}
        searchQuery={state.searchQuery}
        onCheckAllPermissions={dispatch.onToggleAll}
        onSearchQueryChange={dispatch.onSearchQueryChange}
      />
      <div className="roles__all-permissions-table__body">
        {groups.length === 0 ? (
          <div className="roles__all-permissions-table__body__empty">No permissions match your search.</div>
        ) : (
          groups.map(([group, permissions]) => {
            const checkedCount = permissions.reduce(
              (count, permission) => count + (permissionIsChecked(permission) ? 1 : 0),
              0,
            );
            const isGroupAllChecked = checkedCount === permissions.length;
            const isGroupIndeterminate = checkedCount > 0 && !isGroupAllChecked;
            return (
              <Fragment key={group}>
                <div className="roles__all-permissions-table__body__item roles__all-permissions-table__body__item--group-heading">
                  <LabeledCheckbox
                    id={`permission-group--${group}`}
                    label={group}
                    isChecked={isGroupAllChecked}
                    isIndeterminate={isGroupIndeterminate}
                    onChange={() => dispatch.onToggleGroup(group as PermissionGroup)}
                    isRippleDisabled
                  />
                </div>
                {permissions.map((permission) => (
                  <div key={permission.id} className="roles__all-permissions-table__body__item">
                    <LabeledCheckbox
                      id={permission.id}
                      label={permission.description}
                      isChecked={permission.isChecked}
                      onChange={() => dispatch.onTogglePermission(permission.id)}
                      isRippleDisabled
                    />
                  </div>
                ))}
              </Fragment>
            );
          })
        )}
      </div>
      {state.permissionsSelected.all > 0 && (
        <AllPermissionsTableFooter
          allCount={state.permissionsSelected.all}
          hiddenCount={state.permissionsSelected.hidden}
        />
      )}
    </div>
  );
};

export default memo(AllPermissionsTable);
